import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as Sentry from '@sentry/react';

const { REACT_APP_ENV_LABEL = 'unknown' } = process.env;

Sentry.init({
  dsn: 'https://48cc34e91e1f4f8a8406546daa30e140@o1334154.ingest.sentry.io/4503972246388736',
  integrations: [Sentry.browserTracingIntegration()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: REACT_APP_ENV_LABEL === 'production' ? 1.0 : 0,
  environment: REACT_APP_ENV_LABEL,
});

const render = () =>
  ReactDOM.render(<App />, document.getElementById('root') as HTMLElement);

// design-dashboard: with mocks on, seed a fake auth token (before App mounts)
// and start the MSW worker so every request is intercepted at the network layer
// (visible in the Network tab) and answered from the in-memory mock store.
//
// Nothing listens on REACT_APP_API_BASE_URL in this repo — there is no backend.
// The service worker is the *only* thing answering data requests, so if it is
// not controlling the page, every request fails and the entire app renders
// empty: no provider name, no appointments, no tasks, no roster. It still looks
// like a working app, which is what makes this failure so hard to read.
//
// `worker.start()` resolving is not enough to rule that out. A freshly
// registered service worker does not necessarily control the page that
// registered it, and a worker left in a bad state by an earlier session can
// resolve `start()` and then intercept nothing. So we probe before rendering.
const RELOAD_FLAG = 'msw-reload-attempted';

/**
 * Ask the API host a question only the mock layer can answer. The host is not
 * listening, so a network error means the worker is not intercepting — and a
 * relative URL would be useless here, since the dev server happily answers
 * those with index.html.
 */
const mocksAreIntercepting = async (): Promise<boolean> => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/private/provider/self`,
      { headers: { Authorization: 'Bearer design-mock-token' } }
    );
    return res.ok;
  } catch {
    return false;
  }
};

/**
 * Why the worker is not intercepting, in the user's actual browser.
 *
 * The generic "unregister and hard-reload" advice is wrong for the most common
 * cause: service workers only run on `localhost` or HTTPS. CRA prints a LAN URL
 * ("On Your Network: http://192.168.x.x:3100") next to the localhost one, and on
 * that origin the worker never registers — silently, and permanently, no matter
 * how many times you reload or unregister.
 */
const diagnoseMocks = (): { headline: string; fix: string } => {
  const { origin, port, hostname } = window.location;
  const isLocalhost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]';

  if (!isLocalhost) {
    return {
      headline: `This page is served from ${origin}.`,
      fix:
        `Service workers only run on localhost or HTTPS, so the mock layer can ` +
        `never start here. Open http://localhost:${port || '3000'} instead — ` +
        `same server, same app, but an origin the browser will run a worker on.`,
    };
  }
  if (!('serviceWorker' in navigator)) {
    return {
      headline: 'This browser is not exposing the service worker API.',
      fix:
        'That usually means a private/incognito window or a policy blocking ' +
        'service workers. Try a normal window in Chrome.',
    };
  }
  if (!window.isSecureContext) {
    return {
      headline: 'This page is not a secure context.',
      fix: 'Service workers need HTTPS or localhost. Open the localhost URL.',
    };
  }
  return {
    headline: 'The worker is registered but is not controlling this page.',
    fix:
      'Open DevTools → Application → Service Workers, click Unregister, then ' +
      'reload. If it comes back, tick "Update on reload" and reload again.',
  };
};

/** Plain DOM so it shows even if React never mounts. */
const showMocksBanner = () => {
  const { headline, fix } = diagnoseMocks();
  const el = document.createElement('div');
  el.setAttribute('role', 'alert');
  el.style.cssText =
    'position:fixed;inset:0 0 auto 0;z-index:99999;background:#FFF9EC;' +
    'border-bottom:1px solid #FEB117;color:#20262A;padding:10px 16px;' +
    'font:14px/1.45 system-ui,sans-serif';
  el.innerHTML =
    '<strong>Mock data is not loading — every screen will be empty.</strong> ' +
    `${headline} ${fix}`;
  document.body.appendChild(el);
  // The same detail, copy-pasteable, for when the banner is not enough.
  console.error(
    '[mocks] not intercepting',
    JSON.stringify(
      {
        origin: window.location.origin,
        isSecureContext: window.isSecureContext,
        serviceWorkerApi: 'serviceWorker' in navigator,
        controlled: !!navigator.serviceWorker?.controller,
        apiBase: process.env.REACT_APP_API_BASE_URL,
      },
      null,
      2
    )
  );
};

if (process.env.REACT_APP_USE_MOCKS === 'true') {
  if (!window.localStorage.getItem('ayble-auth-token')) {
    window.localStorage.setItem('ayble-auth-token', 'design-mock-token');
  }

  // Check the preconditions before probing. If the browser will not run a
  // service worker on this origin at all, the probe is pointless and a reload
  // can never help — say so immediately instead of bouncing the page first.
  const { hostname } = window.location;
  const canRunWorker =
    (hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' ||
      window.isSecureContext) &&
    'serviceWorker' in navigator;

  if (!canRunWorker) {
    // Safari does not treat http://localhost as a secure context, so the worker
    // can never register there. Answer requests in-page from the same handlers.
    import('./mocks/axiosAdapter')
      .then(({ installMockAdapter }) => installMockAdapter())
      .then(render)
      .catch((e) => {
        console.error('mocks: in-page adapter failed to install', e);
        render();
        showMocksBanner();
      });
  } else
    import('./mocks/browser')
      .then(({ worker }) => worker.start({ onUnhandledRequest: 'bypass' }))
      .then(mocksAreIntercepting)
      .then((intercepting) => {
        if (intercepting) {
          sessionStorage.removeItem(RELOAD_FLAG);
          render();
          return;
        }
        // One reload hands control to the worker in the common case. The flag
        // stops this becoming a loop when something else is wrong.
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          return;
        }
        // Reloading did not help. Rather than presenting an empty app, answer
        // requests in-page from the same handlers.
        import('./mocks/axiosAdapter')
          .then(({ installMockAdapter }) => installMockAdapter())
          .then(render)
          .catch((e) => {
            console.error('mocks: in-page adapter failed to install', e);
            render();
            showMocksBanner();
          });
      })
      .catch((e) => {
        console.error('mocks failed to start', e);
        render();
        showMocksBanner();
      });
} else {
  render();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
